// Preventative Maintenance: templates + property enablement + recurring tasks

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { useCreateManualTask } from "@/hooks/useCleaningTasks";
import { addDays, addMonths, addYears } from "date-fns";
import { format } from "date-fns";

export type PreventativeFrequency = "weekly" | "monthly" | "quarterly" | "bi_annual" | "annual";

export interface PreventativeMaintenanceTemplate {
  id: string;
  template_key: string;
  title: string;
  frequency_default: PreventativeFrequency;
  checklist_steps: { title: string }[];
  display_order: number;
}

export interface PropertyPreventativeMaintenance {
  id: string;
  property_id: string;
  template_id: string | null;
  enabled: boolean;
  frequency: PreventativeFrequency;
  default_assign_type: string | null;
  default_cleaner_id: string | null;
  default_assignee_user_id: string | null;
  checklist_id: string | null;
  next_scheduled_date: string | null;
  created_at: string;
  updated_at: string;
  custom_title: string | null;
  custom_checklist_steps: { title: string }[] | null;
  notes: string | null;
  preventative_maintenance_templates?: PreventativeMaintenanceTemplate | null;
}

const FREQUENCY_LABELS: Record<PreventativeFrequency, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly (every 3 months)",
  bi_annual: "Bi-annual (every 6 months)",
  annual: "Annual",
};

export function getFrequencyLabel(f: PreventativeFrequency): string {
  return FREQUENCY_LABELS[f] ?? f;
}

export function getNextScheduledDate(
  frequency: PreventativeFrequency,
  fromDate: Date
): string {
  switch (frequency) {
    case "weekly":
      return format(addDays(fromDate, 7), "yyyy-MM-dd");
    case "monthly":
      return format(addMonths(fromDate, 1), "yyyy-MM-dd");
    case "quarterly":
      return format(addMonths(fromDate, 3), "yyyy-MM-dd");
    case "bi_annual":
      return format(addMonths(fromDate, 6), "yyyy-MM-dd");
    case "annual":
      return format(addYears(fromDate, 1), "yyyy-MM-dd");
    default:
      return format(addMonths(fromDate, 1), "yyyy-MM-dd");
  }
}

/** Next occurrence on or after today, from anchor (e.g. created_at). Billing-cycle style. */
export function getNextFromAnchor(
  anchorDate: Date,
  frequency: PreventativeFrequency
): string {
  const today = format(new Date(), "yyyy-MM-dd");
  let d = new Date(anchorDate);
  for (let i = 0; i < 500; i++) {
    const nextStr = getNextScheduledDate(frequency, d);
    if (nextStr >= today) return nextStr;
    d = new Date(nextStr + "T12:00:00");
  }
  return format(addYears(new Date(), 1), "yyyy-MM-dd");
}

export function usePreventativeMaintenanceTemplates() {
  return useQuery({
    queryKey: queryKeys.preventativeMaintenance.templates,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("preventative_maintenance_templates")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PreventativeMaintenanceTemplate[];
    },
  });
}

export function usePropertyPreventativeMaintenance(propertyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.preventativeMaintenance.byProperty(propertyId ?? ""),
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("property_preventative_maintenance")
        .select("*, preventative_maintenance_templates(*)")
        .eq("property_id", propertyId);
      if (error) throw error;
      return (data ?? []) as (PropertyPreventativeMaintenance & {
        preventative_maintenance_templates: PreventativeMaintenanceTemplate;
      })[];
    },
    enabled: !!propertyId,
  });
}

export function useEnablePreventativeMaintenance() {
  const queryClient = useQueryClient();
  const createManualTask = useCreateManualTask();

  return useMutation({
    mutationFn: async (params: {
      propertyId: string;
      templateId: string;
      templateKey: string;
      title: string;
      frequency: PreventativeFrequency;
      checklistSteps: { title: string }[];
      defaultAssignType?: "owner" | "cleaner" | "unassigned" | "team";
      defaultCleanerId?: string | null;
      defaultAssigneeUserId?: string | null;
    }) => {
      const {
        propertyId,
        templateId,
        title,
        frequency,
        checklistSteps,
        defaultAssignType,
        defaultCleanerId,
        defaultAssigneeUserId,
      } = params;

      // 0) If already enabled (unique on property_id, template_id), just re-enable and return
      const { data: existing } = await supabase
        .from("property_preventative_maintenance")
        .select("id, checklist_id, enabled")
        .eq("property_id", propertyId)
        .eq("template_id", templateId)
        .maybeSingle();
      if (existing) {
        const { data: updated, error: updateErr } = await supabase
          .from("property_preventative_maintenance")
          .update({
            enabled: true,
            frequency,
            default_assign_type: defaultAssignType ?? null,
            default_cleaner_id: defaultCleanerId ?? null,
            default_assignee_user_id: defaultAssigneeUserId ?? null,
          })
          .eq("id", existing.id)
          .select()
          .single();
        if (updateErr) throw updateErr;
        return { ppm: updated, propertyId };
      }

      // 1) Create a checklist for this template (one room "Maintenance" with tasks from steps)
      const roomsJson = [
        {
          name: "Maintenance",
          tasks: checklistSteps.map((s) => ({
            text: s.title,
            task_type: "cleaning",
            cleaning_settings: {
              require_reference_photo: false,
              require_before_photo: false,
              require_after_photo: false,
            },
          })),
        },
      ];
      const { data: checklist, error: checklistError } = await supabase
        .from("checklists")
        .insert({
          property_id: propertyId,
          name: `Preventative: ${title}`,
          description: `Recurring maintenance – ${title}`,
          rooms: roomsJson,
        })
        .select("id")
        .single();
      if (checklistError || !checklist) throw checklistError ?? new Error("Failed to create checklist");

      const today = format(new Date(), "yyyy-MM-dd");
      const nextDate = getNextScheduledDate(frequency, new Date());

      // 2) Insert property_preventative_maintenance row (or update if 409 — already enabled)
      let ppm: Record<string, unknown>;
      const { data: insertedPpm, error: ppmError } = await supabase
        .from("property_preventative_maintenance")
        .insert({
          property_id: propertyId,
          template_id: templateId,
          enabled: true,
          frequency,
          default_assign_type: defaultAssignType ?? null,
          default_cleaner_id: defaultCleanerId ?? null,
          default_assignee_user_id: defaultAssigneeUserId ?? null,
          checklist_id: checklist.id,
          next_scheduled_date: nextDate,
        })
        .select()
        .single();
      if (ppmError) {
        const isConflict = ppmError.code === "23505" || (ppmError as { status?: number }).status === 409;
        if (isConflict) {
          await supabase.from("checklists").delete().eq("id", checklist.id);
          const { data: updated, error: updateErr } = await supabase
            .from("property_preventative_maintenance")
            .update({
              enabled: true,
              frequency,
              default_assign_type: defaultAssignType ?? null,
              default_cleaner_id: defaultCleanerId ?? null,
              default_assignee_user_id: defaultAssigneeUserId ?? null,
            })
            .eq("property_id", propertyId)
            .eq("template_id", templateId)
            .select()
            .single();
          if (updateErr) throw updateErr;
          ppm = updated as Record<string, unknown>;
        } else {
          throw ppmError;
        }
      } else {
        ppm = insertedPpm as Record<string, unknown>;
      }

      // 3) Create first scheduled task only when we actually inserted (not on conflict)
      const wasInsert = !!insertedPpm;
      if (wasInsert) {
        const { task: createdTask } = await createManualTask.mutateAsync({
          propertyId,
          scheduledDate: today,
          checklistId: checklist.id,
          cleanerId: defaultAssignType === "cleaner" ? defaultCleanerId ?? null : null,
          assigneeUserId: defaultAssignType === "team" ? defaultAssigneeUserId ?? null : null,
          assignedType: defaultAssignType ?? "unassigned",
          notes: `Preventative maintenance: ${title}`,
        });

        const taskId = (createdTask as { id?: string })?.id;
        if (taskId) {
          await supabase
            .from("cleaning_tasks")
            .update({
              task_type: "preventative_maintenance",
              preventative_maintenance_id: ppm.id,
            })
            .eq("id", taskId);
        }
      }

      return { ppm, propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.preventativeMaintenance.byProperty(data.propertyId),
      });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
}

export function useUpdatePropertyPreventativeMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: string;
      propertyId: string;
      enabled?: boolean;
      frequency?: PreventativeFrequency;
      default_assign_type?: string | null;
      default_cleaner_id?: string | null;
      default_assignee_user_id?: string | null;
      next_scheduled_date?: string | null;
      notes?: string | null;
    }) => {
      const { id, propertyId, ...updates } = params;
      const { error } = await supabase
        .from("property_preventative_maintenance")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.preventativeMaintenance.byProperty(data.propertyId),
      });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
}

export function useCreateCustomPreventativeMaintenance() {
  const queryClient = useQueryClient();
  const createManualTask = useCreateManualTask();

  return useMutation({
    mutationFn: async (params: {
      propertyId: string;
      title: string;
      frequency: PreventativeFrequency;
      steps: { title: string }[];
      defaultAssignType?: "owner" | "cleaner" | "unassigned" | "team";
      defaultCleanerId?: string | null;
      defaultAssigneeUserId?: string | null;
      notes?: string | null;
      next_scheduled_date?: string | null;
    }) => {
      const {
        propertyId,
        title,
        frequency,
        steps,
        defaultAssignType,
        defaultCleanerId,
        defaultAssigneeUserId,
        notes,
        next_scheduled_date: nextDateParam,
      } = params;

      const roomsJson = [
        {
          name: "Maintenance",
          tasks: steps.map((s) => ({
            text: s.title,
            task_type: "cleaning",
            cleaning_settings: {
              require_reference_photo: false,
              require_before_photo: false,
              require_after_photo: false,
            },
          })),
        },
      ];
      const { data: checklist, error: checklistError } = await supabase
        .from("checklists")
        .insert({
          property_id: propertyId,
          name: `Preventative: ${title}`,
          description: `Recurring maintenance – ${title}`,
          rooms: roomsJson,
        })
        .select("id")
        .single();
      if (checklistError || !checklist) throw checklistError ?? new Error("Failed to create checklist");

      const firstDueDate = nextDateParam ?? format(new Date(), "yyyy-MM-dd");

      const { data: ppm, error: ppmError } = await supabase
        .from("property_preventative_maintenance")
        .insert({
          property_id: propertyId,
          template_id: null,
          custom_title: title,
          custom_checklist_steps: steps,
          enabled: true,
          frequency,
          default_assign_type: defaultAssignType ?? null,
          default_cleaner_id: defaultCleanerId ?? null,
          default_assignee_user_id: defaultAssigneeUserId ?? null,
          checklist_id: checklist.id,
          next_scheduled_date: firstDueDate,
          notes: notes ?? null,
        })
        .select()
        .single();
      if (ppmError) throw ppmError;

      const { task: createdTask } = await createManualTask.mutateAsync({
        propertyId,
        scheduledDate: firstDueDate,
        checklistId: checklist.id,
        cleanerId: defaultAssignType === "cleaner" ? defaultCleanerId ?? null : null,
        assigneeUserId: defaultAssignType === "team" ? defaultAssigneeUserId ?? null : null,
        assignedType: defaultAssignType ?? "unassigned",
        notes: `Preventative maintenance: ${title}`,
      });

      const taskId = (createdTask as { id?: string })?.id;
      if (taskId) {
        await supabase
          .from("cleaning_tasks")
          .update({
            task_type: "preventative_maintenance",
            preventative_maintenance_id: ppm.id,
          })
          .eq("id", taskId);
      }

      // Slice 40 — Team: in-app notify when due today or overdue
      if (defaultAssignType === "team" || defaultAssignType === "owner") {
        const today = format(new Date(), "yyyy-MM-dd");
        if (firstDueDate <= today) {
          const { data: prop } = await supabase
            .from("properties")
            .select("owner_id, name")
            .eq("id", propertyId)
            .maybeSingle();
          if (prop?.owner_id) {
            await supabase.from("owner_notifications").insert({
              owner_id: prop.owner_id,
              property_id: propertyId,
              cleaning_task_id: taskId ?? null,
              type: "preventative_due",
              title: `${prop.name || "Property"}: ${title}`,
              message: firstDueDate === today ? "Due today" : `Due ${firstDueDate}`,
              is_read: false,
            });
          }
        }
      }

      return { ppm, propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.preventativeMaintenance.byProperty(data.propertyId),
      });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
}

export function useDisablePreventativeMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      const { error } = await supabase
        .from("property_preventative_maintenance")
        .update({ enabled: false })
        .eq("id", id);
      if (error) throw error;
      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.preventativeMaintenance.byProperty(data.propertyId),
      });
    },
  });
}

export function useScheduleNextPreventativeTask() {
  const queryClient = useQueryClient();
  const createManualTask = useCreateManualTask();

  return useMutation({
    mutationFn: async (params: {
      propertyId: string;
      ppm: PropertyPreventativeMaintenance & {
        preventative_maintenance_templates?: PreventativeMaintenanceTemplate;
      };
    }) => {
      const { propertyId, ppm } = params;
      if (!ppm.checklist_id || !ppm.enabled) throw new Error("Not enabled or no checklist");
      const title = ppm.custom_title ?? ppm.preventative_maintenance_templates?.title ?? "Maintenance";
      const nextDate = ppm.next_scheduled_date ?? format(new Date(), "yyyy-MM-dd");

      await createManualTask.mutateAsync({
        propertyId,
        scheduledDate: nextDate,
        checklistId: ppm.checklist_id,
        cleanerId: ppm.default_assign_type === "cleaner" ? ppm.default_cleaner_id : null,
        assigneeUserId: ppm.default_assign_type === "team" ? ppm.default_assignee_user_id : null,
        assignedType: (ppm.default_assign_type as "owner" | "cleaner" | "unassigned" | "team") ?? "unassigned",
        notes: `Preventative maintenance: ${title}`,
      });

      const nextNext = getNextScheduledDate(
        ppm.frequency as PreventativeFrequency,
        new Date(nextDate)
      );
      await supabase
        .from("property_preventative_maintenance")
        .update({ next_scheduled_date: nextNext })
        .eq("id", ppm.id);

      const { data: tasks } = await supabase
        .from("cleaning_tasks")
        .select("id")
        .eq("property_id", propertyId)
        .eq("scheduled_date", nextDate)
        .eq("checklist_id", ppm.checklist_id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (tasks?.[0]) {
        await supabase
          .from("cleaning_tasks")
          .update({
            task_type: "preventative_maintenance",
            preventative_maintenance_id: ppm.id,
          })
          .eq("id", tasks[0].id);
      }

      return { propertyId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.preventativeMaintenance.byProperty(data.propertyId),
      });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", data.propertyId] });
    },
  });
}
